import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  Brain,
  Trophy,
  ArrowRight,
  Calendar,
  Headphones,
  Mail,
  ShieldCheck,
  Sparkles,
  Target,
  ExternalLink,
  Award,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { CALENDLY_URL, ELVTR_CERTIFICATE_URL } from '../lib/siteConfig';
import { useDocumentHead } from './blog/useDocumentHead';

/**
 * /about — Bio Hub (Phase 21.5)
 * ---------------------------------------------------------------------------
 * Dedicated identity page for Coach Varsay Lewis, M.Ed., and the
 * Apex Athletic Mind methodology. All bio facts are sourced directly from
 * the live marketing site (CredibilitySection, TrustStrip, PlaybookPromo)
 * — no fabrication. Page is linked from the site footer + nav, and is the
 * canonical destination for any "About Coach Lewis" link across emails,
 * PDFs, and social bios.
 *
 * Structure:
 *   1. Hero: headshot + name + role
 *   2. Credentials row
 *   3. Coaching philosophy
 *   4. Apex Six methodology callout
 *   5. Personal story / journey
 *   6. Notable partnerships
 *   7. Conversion CTAs (Discovery Call + Locker Room)
 *   8. Legal entity footer
 */

const HEADSHOT_URL =
  'https://customer-assets.emergentagent.com/job_apex-athletes-mind/artifacts/8444i0fx_Polished%20headshot.png';

const CREDENTIALS = [
  {
    icon: GraduationCap,
    label: 'M.Ed., School Counseling',
    detail: 'Wilmington University · Elementary & Secondary',
  },
  {
    icon: Brain,
    label: 'Sport Psychology Certified',
    detail: 'ELVTR · Under Dr. Sara Hickmann',
    href: ELVTR_CERTIFICATE_URL,
    hrefLabel: 'View certificate',
  },
  {
    icon: Trophy,
    label: 'Championship Soccer Coach',
    detail: 'NJ Non-Public B State Champions (Asst. Head Coach, MKA)',
  },
];

function trackAboutCta(label) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'about_cta_click', { label });
    }
  } catch {
    /* analytics is best-effort */
  }
}

export default function AboutPage() {
  useDocumentHead({
    title: 'About Coach Varsay Lewis, M.Ed. — Apex Athletic Mind',
    description:
      'Coach Varsay Lewis, M.Ed. — sport psychology certified, championship soccer coach, and founder of Apex Athletic Mind Consulting LLC. Mental performance training for middle school, high school, and college athletes in NJ + virtually nationwide.',
    canonical: '/about',
    image: HEADSHOT_URL,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Varsay Lewis',
      jobTitle: 'Mental Performance Consultant, School Counselor, Championship Soccer Coach',
      image: HEADSHOT_URL,
      worksFor: {
        '@type': 'Organization',
        name: 'Apex Athletic Mind Consulting LLC',
        url: 'https://apexathleticmind.com',
      },
      alumniOf: {
        '@type': 'CollegeOrUniversity',
        name: 'Wilmington University',
      },
      knowsAbout: [
        'Sport Psychology',
        'Mental Performance',
        'Athletic Confidence',
        'Pre-Game Mental Preparation',
        'Apex Six Framework',
      ],
      url: 'https://apexathleticmind.com/about',
    },
  });

  return (
    <main className="min-h-screen bg-white" data-testid="about-page">
      {/* Header strip — small brand bar */}
      <div className="bg-[#004C54] text-white/85 text-[12px]">
        <div className="max-w-[1120px] mx-auto px-5 py-2">
          <Link
            to="/"
            className="font-mono-accent uppercase tracking-[0.18em] hover:text-white"
            data-testid="about-back-home"
          >
            ← apexathleticmind.com
          </Link>
        </div>
      </div>

      {/* HERO — name + headshot + role */}
      <section className="bg-gradient-to-b from-[#F5F3EE] to-white py-14 sm:py-20" data-testid="about-hero">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 grid sm:grid-cols-[1.3fr_1fr] gap-10 sm:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3">
              <Sparkles className="h-3 w-3" />
              About Coach Lewis
            </div>
            <h1
              className="font-heading text-[2.3rem] sm:text-[3rem] font-bold tracking-tight leading-[1.05] text-[#003a40] mb-4"
              data-testid="about-name"
            >
              Varsay Lewis, M.Ed.
            </h1>
            <p
              className="text-[16.5px] sm:text-[18px] text-[#1a1a1a]/75 leading-[1.55] max-w-[58ch] mb-6"
              data-testid="about-tagline"
            >
              School counselor by training. Sport psychology professional by certification.
              Championship soccer coach by trade. I help middle school, high school, and college
              athletes compete with confidence, focus, and composure when the game gets loud.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                asChild
                className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 px-5 font-semibold"
                data-testid="about-hero-cta-discovery"
              >
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackAboutCta('Discovery Call')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book a Discovery Call
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-[#004C54] text-[#004C54] hover:bg-[#004C54]/8 h-11 px-5 font-semibold"
                data-testid="about-hero-cta-quiz"
              >
                <Link to="/quiz" onClick={() => trackAboutCta('Quiz')}>
                  Take the 2-Min Quiz <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="order-first sm:order-last">
            <div className="relative max-w-[340px] mx-auto sm:max-w-none">
              <img
                src={HEADSHOT_URL}
                alt="Varsay Lewis, M.Ed. — Founder, Apex Athletic Mind"
                className="rounded-2xl shadow-[0_20px_60px_rgba(0,76,84,0.18)] ring-1 ring-[#004C54]/10 w-full"
                data-testid="about-headshot"
              />
              <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 inline-flex items-center gap-1.5 bg-[#004C54] text-white px-3 py-1.5 rounded-full text-[10px] font-mono-accent uppercase tracking-[0.2em] font-bold shadow-lg">
                <ShieldCheck className="h-3 w-3 text-[#4FBCC7]" />
                Cherry Hill, NJ · Virtual nationwide
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="py-12 sm:py-16 bg-white" data-testid="about-credentials">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-4">
            Credentials
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {CREDENTIALS.map((c) => {
              const Icon = c.icon;
              const isLink = Boolean(c.href);
              const cardClasses =
                'group rounded-2xl border border-[#004C54]/15 bg-[#F5F3EE]/60 p-5 transition-colors ' +
                (isLink
                  ? 'hover:border-[#004C54]/60 hover:bg-[#F5F3EE] cursor-pointer block'
                  : 'hover:border-[#004C54]/30');
              const testId = `about-credential-${c.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')}`;

              const inner = (
                <>
                  <div className="flex items-start justify-between mb-3">
                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#004C54] text-white">
                      <Icon className="h-4 w-4" />
                    </div>
                    {isLink && (
                      <span
                        className="inline-flex items-center gap-1 font-mono-accent uppercase tracking-[0.18em] text-[9px] font-bold text-[#004C54]/70 group-hover:text-[#004C54]"
                        data-testid={`${testId}-verify-badge`}
                      >
                        <ShieldCheck className="h-3 w-3" />
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="font-heading text-[15px] font-bold text-[#003a40] leading-tight">
                    {c.label}
                  </div>
                  <div className="text-[12.5px] text-[#3a3a3a]/85 mt-1 leading-snug">{c.detail}</div>
                  {isLink && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#004C54] group-hover:gap-1.5 transition-[gap]">
                      <Award className="h-3.5 w-3.5" />
                      {c.hrefLabel}
                      <ExternalLink className="h-3 w-3" />
                    </div>
                  )}
                </>
              );

              return isLink ? (
                <a
                  key={c.label}
                  href={c.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackAboutCta(`Credential · ${c.label}`)}
                  className={cardClasses}
                  data-testid={testId}
                  aria-label={`${c.label} — ${c.hrefLabel}`}
                >
                  {inner}
                </a>
              ) : (
                <div key={c.label} className={cardClasses} data-testid={testId}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-12 sm:py-16 bg-[#0a0d0e] text-white" data-testid="about-philosophy">
        <div className="max-w-[860px] mx-auto px-5 sm:px-8">
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#4FBCC7] mb-3">
            Coaching Philosophy
          </div>
          <h2 className="font-heading text-[1.8rem] sm:text-[2.2rem] font-bold tracking-tight leading-[1.15] mb-5">
            Talent is the floor. Mindset is the ceiling.
          </h2>
          <div className="space-y-4 text-[15px] sm:text-[16px] text-white/75 leading-[1.7]">
            <p>
              Most athletes plateau because their body has caught up to their craft but their mind
              hasn&rsquo;t. They train the shot, the swing, the sprint &mdash; and skip the muscle
              that actually decides games: the one between their ears.
            </p>
            <p>
              The work I do isn&rsquo;t therapy. It isn&rsquo;t motivational speaking. It&rsquo;s
              the same skills-based training your athlete does for their sport, applied to the
              mental side of competition: composure, confidence, awareness, focus, resilience, and
              preparation. The Apex Six.
            </p>
            <p>
              I&rsquo;m not interested in cliches. I&rsquo;m interested in giving athletes
              specific, rehearsable protocols they can run on a bus ride, in the locker room, or
              between innings &mdash; and watch their performance change.
            </p>
          </div>
        </div>
      </section>

      {/* APEX SIX METHODOLOGY */}
      <section className="py-12 sm:py-16 bg-[#F5F3EE]" data-testid="about-methodology">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
          <div className="grid sm:grid-cols-[1fr_1.3fr] gap-8 sm:gap-12 items-center">
            <div>
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3">
                The Methodology
              </div>
              <h2 className="font-heading text-[1.7rem] sm:text-[2rem] font-bold tracking-tight text-[#003a40] mb-4 leading-[1.15]">
                The Apex Six.
              </h2>
              <p className="text-[14.5px] text-[#1a1a1a]/75 leading-[1.65] mb-5 max-w-[44ch]">
                Six dimensions of mental performance, built into rehearsable protocols. Every
                athlete I work with gets a scored profile across all six, a Primary Action to
                focus on, and a printable IDP they can pin in their locker.
              </p>
              <Button
                asChild
                className="bg-[#004C54] hover:bg-[#003a40] text-white h-10 px-4 font-semibold"
                data-testid="about-methodology-cta"
              >
                <Link to="/assessment" onClick={() => trackAboutCta('IDP')}>
                  <Target className="h-4 w-4 mr-2" />
                  See the IDP &mdash; $27
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="about-apex-six-grid">
              {[
                'Composure',
                'Confidence',
                'Awareness',
                'Focus',
                'Resilience',
                'Preparation',
              ].map((dim, i) => (
                <div
                  key={dim}
                  className="rounded-xl bg-white p-3 sm:p-4 ring-1 ring-[#004C54]/10 text-center"
                >
                  <div className="font-mono-accent text-[9.5px] uppercase tracking-[0.2em] text-[#4FBCC7] font-bold">
                    #{String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="font-heading text-[14px] sm:text-[15px] font-bold text-[#003a40] mt-1">
                    {dim}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-12 sm:py-16 bg-white" data-testid="about-story">
        <div className="max-w-[860px] mx-auto px-5 sm:px-8">
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3">
            The Why
          </div>
          <h2 className="font-heading text-[1.8rem] sm:text-[2.2rem] font-bold tracking-tight leading-[1.15] text-[#003a40] mb-5">
            From the sideline to the in-between.
          </h2>
          <div className="space-y-4 text-[15.5px] text-[#1a1a1a]/80 leading-[1.7]">
            <p>
              I spent two decades competing &mdash; through high school and beyond &mdash; and the
              same number of years coaching club and high school athletes. As Asst. Head Coach at
              Montclair Kimberley Academy, I helped lead the boys soccer program to a New Jersey
              Non-Public B State Championship. Before that, I was Head Coach of both boys&rsquo;
              and girls&rsquo; soccer at Camden Catholic High School.
            </p>
            <p>
              What I saw, over and over, was the same kid: technically gifted, physically ready,
              and mentally unprepared for the moment. They&rsquo;d crumble in the second half. Lose
              confidence after one bad rep. Talk themselves out of the shot they had practiced a
              thousand times.
            </p>
            <p>
              My school counseling work (M.Ed., Wilmington University) gave me the foundation in
              behavioral skills. My{' '}
              <a
                href={ELVTR_CERTIFICATE_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackAboutCta('Story · ELVTR Certificate')}
                className="font-semibold text-[#004C54] underline decoration-[#4FBCC7]/60 decoration-2 underline-offset-2 hover:decoration-[#004C54]"
                data-testid="about-story-elvtr-link"
              >
                ELVTR sport psychology certification
              </a>{' '}
              &mdash; under Dr. Sara Hickmann &mdash; gave me the science. The Apex Six framework
              is what came out of combining both with what I&rsquo;d already learned from 20+ years
              on the field: that the mental side of sport is a teachable skill, not a personality
              trait.
            </p>
            <p>
              Apex Athletic Mind is built so that athletes everywhere can get those reps &mdash;
              not just the ones whose school can afford a sport psych on staff.
            </p>
          </div>
        </div>
      </section>

      {/* PARTNERSHIPS */}
      <section className="py-12 sm:py-16 bg-[#F5F3EE]/50 border-y border-[#004C54]/10" data-testid="about-partnerships">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3 text-center">
            Where the work happens
          </div>
          <h2 className="font-heading text-[1.5rem] sm:text-[1.85rem] font-bold tracking-tight text-[#003a40] text-center mb-8 leading-[1.2]">
            Schools, teams, and athletes I&rsquo;ve worked with.
          </h2>
          <div className="grid sm:grid-cols-3 gap-4 text-center">
            <div className="rounded-2xl bg-white p-5 ring-1 ring-[#004C54]/10" data-testid="about-partnership-mka">
              <Trophy className="h-5 w-5 text-[#4FBCC7] mx-auto mb-2" />
              <div className="font-heading text-[15px] font-bold text-[#003a40]">
                Montclair Kimberley Academy
              </div>
              <div className="text-[12px] text-[#1a1a1a]/65 mt-1 leading-snug">
                Asst. Head Coach, Boys Soccer
                <br />
                NJ Non-Public B State Champions
              </div>
            </div>
            <div className="rounded-2xl bg-white p-5 ring-1 ring-[#004C54]/10" data-testid="about-partnership-camden">
              <Trophy className="h-5 w-5 text-[#4FBCC7] mx-auto mb-2" />
              <div className="font-heading text-[15px] font-bold text-[#003a40]">
                Camden Catholic High School
              </div>
              <div className="text-[12px] text-[#1a1a1a]/65 mt-1 leading-snug">
                Head Coach, Boys & Girls Soccer
                <br />
                Multi-season tenure
              </div>
            </div>
            <div className="rounded-2xl bg-white p-5 ring-1 ring-[#004C54]/10" data-testid="about-partnership-virtual">
              <Brain className="h-5 w-5 text-[#4FBCC7] mx-auto mb-2" />
              <div className="font-heading text-[15px] font-bold text-[#003a40]">
                Virtual + Tri-State
              </div>
              <div className="text-[12px] text-[#1a1a1a]/65 mt-1 leading-snug">
                1:1 sessions, team workshops,
                <br />
                and the Apex AI Coach platform
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="py-14 sm:py-20 bg-[#0a0d0e] text-white" data-testid="about-cta-section">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
          <div className="text-center mb-8">
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#4FBCC7] mb-3">
              Three ways to start
            </div>
            <h2 className="font-heading text-[1.8rem] sm:text-[2.3rem] font-bold tracking-tight leading-[1.1] max-w-[28ch] mx-auto">
              Pick the door that fits where you are right now.
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackAboutCta('Discovery Call')}
              data-testid="about-cta-discovery"
              className="group rounded-2xl bg-white text-[#003a40] p-6 hover:scale-[1.01] transition-transform"
            >
              <Calendar className="h-5 w-5 text-[#004C54] mb-2" />
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] font-bold text-[#004C54] mb-1.5">
                30-min · free
              </div>
              <div className="font-heading text-[1.2rem] font-bold mb-1.5">Discovery Call</div>
              <div className="text-[12.5px] text-[#1a1a1a]/70 leading-snug mb-3">
                For parents and athletes ready to talk it through. Bring your scenario &mdash;
                we&rsquo;ll map the plan.
              </div>
              <span className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#004C54]">
                Book a call <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </a>
            <Link
              to="/assessment"
              onClick={() => trackAboutCta('IDP')}
              data-testid="about-cta-idp"
              className="group rounded-2xl bg-[#004C54] text-white p-6 ring-1 ring-[#4FBCC7]/30 hover:scale-[1.01] transition-transform"
            >
              <Target className="h-5 w-5 text-[#4FBCC7] mb-2" />
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] font-bold text-[#4FBCC7] mb-1.5">
                $27 · printable
              </div>
              <div className="font-heading text-[1.2rem] font-bold mb-1.5">The Apex Six IDP</div>
              <div className="text-[12.5px] text-white/75 leading-snug mb-3">
                A scored mental performance profile across all six dimensions. Your Primary
                Action + a printable PDF.
              </div>
              <span className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#4FBCC7]">
                Start my IDP <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
            <Link
              to="/locker-room"
              onClick={() => trackAboutCta('Locker Room')}
              data-testid="about-cta-locker"
              className="group rounded-2xl bg-gradient-to-br from-[#4FBCC7]/20 to-[#004C54]/30 text-white p-6 ring-1 ring-[#4FBCC7]/30 hover:scale-[1.01] transition-transform"
            >
              <Headphones className="h-5 w-5 text-[#4FBCC7] mb-2" />
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] font-bold text-[#4FBCC7] mb-1.5">
                Free · no signup
              </div>
              <div className="font-heading text-[1.2rem] font-bold mb-1.5">The Locker Room</div>
              <div className="text-[12.5px] text-white/75 leading-snug mb-3">
                Pre-game playlists, the Apex Six notecards, and a free preview of the membership
                library.
              </div>
              <span className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#4FBCC7]">
                Open the locker <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Legal entity footer */}
      <section className="py-10 bg-white border-t border-[#004C54]/10" data-testid="about-legal">
        <div className="max-w-[860px] mx-auto px-5 sm:px-8">
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[9px] text-[#004C54]/70 font-semibold mb-2">
            About the Company
          </div>
          <p className="text-[13px] text-[#3a3a3a] leading-[1.7] max-w-[68ch] flex items-start gap-2.5">
            <Mail className="h-3.5 w-3.5 mt-0.5 text-[#004C54]/60 shrink-0" />
            <span>
              Apex Athletic Mind is the registered brand of{' '}
              <strong className="font-semibold text-[#1a1a1a]">
                Apex Athletic Mind Consulting LLC
              </strong>
              , a New Jersey Limited Liability Company. Founded by Varsay Lewis, M.Ed. Operations
              based in Cherry Hill, NJ. Services delivered in person throughout the Tri-State area
              and virtually nationwide via the Apex AI Coach app and remote consulting sessions.
            </span>
          </p>
        </div>
      </section>
    </main>
  );
}
